import { useCallback, useEffect, useRef, useState } from "react";

const ZOOM_FACTOR = 0.003;
const MIN_CAMERA_DISTANCE = 100;
const MAX_CAMERA_DISTANCE = 20_000_000;

interface UseMapKitOptions {
  libraries?: string[];
  wheelZoom?: boolean;
}

interface UseMapKitReturn {
  handleMapKitLoad: () => void;
  isMapInitialized: boolean;
  mapContainerRef: React.RefObject<HTMLDivElement | null>;
  mapRef: React.RefObject<mapkit.Map | null>;
}

export function useMapKitLoad({
  libraries = ["annotations", "map"],
  wheelZoom = false,
}: UseMapKitOptions = {}): UseMapKitReturn {
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapkit.Map>(null);
  const [isMapInitialized, setIsMapInitialized] = useState(false);

  const areLibrariesLoaded = useCallback(() => {
    return (
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      typeof mapkit !== "undefined" &&
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      // biome-ignore lint/suspicious/noExplicitAny: legacy
      (mapkit as any).loadedLibraries &&
      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      // biome-ignore lint/suspicious/noExplicitAny: legacy
      libraries.every((lib) => (mapkit as any).loadedLibraries.includes(lib))
    );
  }, [libraries]);

  const checkLibrariesLoaded = useCallback(() => {
    if (areLibrariesLoaded()) {
      setIsMapInitialized(true);
      return;
    }

    setTimeout(checkLibrariesLoaded, 100);
  }, [areLibrariesLoaded]);

  const handleMapKitLoad = useCallback(() => {
    // biome-ignore lint/correctness/noUndeclaredVariables: legacy
    mapkit.init({
      authorizationCallback(done) {
        done(process.env.MAPKIT_JS_TOKEN || "");
      },
    });

    // biome-ignore lint/correctness/noUndeclaredVariables: legacy
    mapkit.addEventListener("configuration-change", (event) => {
      if (event.status === "Initialized") {
        checkLibrariesLoaded();
      }
    });
  }, [checkLibrariesLoaded]);

  useEffect(() => {
    if (areLibrariesLoaded()) {
      setIsMapInitialized(true);
    }
  }, [areLibrariesLoaded]);

  useEffect(() => {
    const container = mapContainerRef.current;
    if (!(wheelZoom && container)) {
      return;
    }

    let pendingDelta = 0;
    let lastEvent: WheelEvent | null = null;
    let rafId = 0;

    const applyZoom = () => {
      rafId = 0;
      const map = mapRef.current;
      if (!(map && lastEvent)) {
        pendingDelta = 0;
        lastEvent = null;
        return;
      }

      const distance = map.cameraDistance;
      const newDistance = distance * (1 + pendingDelta);
      const clamped = Math.min(
        MAX_CAMERA_DISTANCE,
        Math.max(MIN_CAMERA_DISTANCE, newDistance)
      );

      const ratio = clamped / distance;
      const cursorPoint = new DOMPoint(lastEvent.pageX, lastEvent.pageY);
      const cursorCoord = map.convertPointOnPageToCoordinate(cursorPoint);
      const center = map.center;

      const newLat =
        cursorCoord.latitude + ratio * (center.latitude - cursorCoord.latitude);
      const newLng =
        cursorCoord.longitude +
        ratio * (center.longitude - cursorCoord.longitude);

      // biome-ignore lint/correctness/noUndeclaredVariables: legacy
      map.setCenterAnimated(new mapkit.Coordinate(newLat, newLng), false);
      map.setCameraDistanceAnimated(clamped, false);

      pendingDelta = 0;
      lastEvent = null;
    };

    const handleWheel = (event: WheelEvent) => {
      if (!mapRef.current) {
        return;
      }

      event.preventDefault();

      pendingDelta += event.deltaY * ZOOM_FACTOR;
      lastEvent = event;

      if (!rafId) {
        rafId = requestAnimationFrame(applyZoom);
      }
    };

    container.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      container.removeEventListener("wheel", handleWheel);
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [wheelZoom]);

  return {
    handleMapKitLoad,
    isMapInitialized,
    mapContainerRef,
    mapRef,
  };
}
